import SEO from '../components/SEO'
import ScrollReveal from '../components/ScrollReveal'
import CTASection from '../components/CTASection'
import './About.css'
import '../styles/shared-hero.css'

const values = [
  {
    title: 'Pragmatism Over Dogma',
    description: 'The right solution depends on context—company size, team capability, budget constraints, and growth trajectory. We adapt our approach to fit your reality, not force your reality into a predetermined framework.',
  },
  {
    title: 'Technology as a Means',
    description: 'Technology serves business objectives, not the reverse. Every recommendation ties back to measurable outcomes: revenue growth, cost reduction, risk mitigation, or operational efficiency.',
  },
  {
    title: 'Sustainable Solutions',
    description: 'We build for the long term. Quick fixes that create technical debt are avoided in favour of approaches that scale gracefully and can be maintained by your team.',
  },
  {
    title: 'Knowledge Transfer',
    description: 'Our goal is to make ourselves unnecessary. We document decisions, train your team, and build internal capability so you can eventually manage without external support.',
  },
]

const experience = [
  'Built and led engineering teams of up to 50+ people',
  'Architected systems processing millions of transactions',
  'Led digital transformations for established enterprises',
  'Advised on £10M+ technology investments',
  'Delivered critical projects under regulatory pressure',
  'Navigated complex stakeholder environments',
]

function About() {
  return (
    <>
      <SEO 
        title="About" 
        description="Learn about Claverton 42's approach to fractional CTO services and our commitment to pragmatic technology leadership."
        path="/about"
      />
      
      {/* Hero Section */}
      <section className="about-hero page-hero">
        <div className="page-hero-content about-hero-content">
          <ScrollReveal>
            <h1>Technology leadership <em>rooted in experience</em></h1>
          </ScrollReveal>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="philosophy-section">
        <div className="section-grid">
          <div className="section-label">
            Philosophy
            <span></span>
          </div>
          <ScrollReveal className="philosophy-content">
            <h2>
              We believe that effective technology leadership is about <em>judgment</em>, not just technical knowledge
            </h2>
            <div className="philosophy-text">
              <p>
                After decades building and scaling technology organisations, we've learned that the hardest challenges rarely have purely technical solutions. They require understanding business context, managing organisational dynamics, and making decisions with incomplete information.
              </p>
              <p>
                Our fractional model exists because we saw a gap: too many growing companies struggle without senior technical guidance, yet aren't ready for—or don't need—a full-time CTO. By working fractionally, we provide that leadership precisely when and how it's needed.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Values Section */}
      <section className="values-section">
        <div className="section-grid">
          <div className="section-label">
            Values
            <span></span>
          </div>
          <div className="values-content">
            <ScrollReveal>
              <h2>Principles that guide our work</h2>
            </ScrollReveal>
            <div className="values-grid">
              {values.map((value, index) => (
                <ScrollReveal key={value.title} delay={index * 100}>
                  <div className="value-card">
                    <h3>{value.title}</h3>
                    <p>{value.description}</p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="experience-section">
        <div className="section-grid">
          <div className="section-label">
            Experience
            <span></span>
          </div>
          <div className="experience-content">
            <ScrollReveal>
              <h2>Decades of building and scaling technology</h2>
            </ScrollReveal>
            <ScrollReveal>
              <p className="experience-intro">
                Our background spans startups to enterprises, covering the full spectrum of technology leadership challenges:
              </p>
            </ScrollReveal>
            <ScrollReveal>
              <ul className="experience-list">
                {experience.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="approach-section">
        <div className="section-grid">
          <div className="section-label">
            Approach
            <span></span>
          </div>
          <ScrollReveal className="approach-content">
            <h2>How we work</h2>
            <div className="approach-text">
              <p>
                Every engagement begins with understanding—your business, your constraints, your ambitions. We don't arrive with a fixed methodology to impose; we develop an approach tailored to your situation.
              </p>
              <p>
                Typically, this involves a mix of strategic advisory (roadmapping, architecture decisions, vendor selection), operational support (process design, hiring, team structure), and hands-on delivery (code review, technical oversight, crisis response).
              </p>
              <p>
                The balance shifts as your needs evolve. Early-stage companies often need more hands-on technical direction; scaleups benefit from organisational design and process; established companies may need transformation guidance or interim leadership during transitions.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection />
    </>
  )
}

export default About
